import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { CAPACITY_HOURS_DISPLAY_DECIMALS } from '../../../constants/capacityConstants';
import { roundToDecimals } from '../../../utils/numberUtils';
import { evaluateKpiSeverity } from './kpiSeverity';
import type { KpiSeverity, ProfessionHours } from './types';
import styles from './TimeDistributionPopover.module.css';

interface TimeDistributionPopoverProps {
  professions: readonly ProfessionHours[];
}

const deltaClass = (severity: KpiSeverity): string => {
  switch (severity) {
    case 'OK':
      return styles.deltaOk;
    case 'WARNING':
      return styles.deltaWarning;
    case 'EXCEEDED':
      return styles.deltaError;
    default: {
      const _exhaustive: never = severity;
      throw new Error(`Unhandled KpiSeverity: ${_exhaustive as string}`);
    }
  }
};

const formatDelta = (delta: number): string => {
  const rounded = roundToDecimals(delta, CAPACITY_HOURS_DISPLAY_DECIMALS);
  return rounded > 0 ? `+${rounded}` : `${rounded}`;
};

export const TimeDistributionPopover = ({ professions }: TimeDistributionPopoverProps) => {
  const { t } = useTranslation();

  return (
    <div className={styles.popover}>
      <div className={styles.title}>{t('gantt-time-distribution-title')}</div>
      <div className={styles.grid}>
        {professions.map((entry) => {
          const severity = evaluateKpiSeverity(entry.planned, entry.threshold);
          const delta = entry.planned - entry.threshold.limit;
          // Per-row local scale: the longer of planned/required maps to the
          // full grid-cell width. The other shrinks proportionally.
          const maxValue = Math.max(entry.planned, entry.threshold.limit);
          const requiredPercent = (entry.threshold.limit / maxValue) * 100;
          const plannedPercent = (entry.planned / maxValue) * 100;
          return (
            <Fragment key={entry.profession.id}>
              <div className={styles.name}>{entry.profession.name}</div>
              <div className={styles.barWrapper}>
                <div className={styles.lavender} style={{ width: `${requiredPercent}%` }} />
                {/* At 0 planned hours the bar collapses to a padding-wide nub
                    with clipped text, so it is omitted entirely. */}
                {entry.planned > 0 && (
                  <div className={styles.blue} style={{ width: `${plannedPercent}%` }}>
                    {t('gantt-hours-long', {
                      n: roundToDecimals(entry.planned, CAPACITY_HOURS_DISPLAY_DECIMALS)
                    })}
                  </div>
                )}
              </div>
              <div className={styles.threshold}>
                {t('gantt-hours-short', {
                  n: roundToDecimals(entry.threshold.limit, CAPACITY_HOURS_DISPLAY_DECIMALS)
                })}
              </div>
              <div className={`${styles.delta} ${deltaClass(severity)}`}>{formatDelta(delta)}</div>
            </Fragment>
          );
        })}
      </div>
      <div className={styles.legend}>
        <span className={styles.legendItem}>
          <span className={`${styles.legendDot} ${styles.legendDotPlanned}`} />
          {t('gantt-planned')}
        </span>
        <span className={styles.legendItem}>
          <span className={`${styles.legendDot} ${styles.legendDotRequired}`} />
          {t('gantt-required')}
        </span>
      </div>
    </div>
  );
};
